/**
 * `DetailDialogShell` — shared modal frame for the two Lexiun Agents
 * detail dialogs (`MissionDetailDialog`, `HarnessDetailDialog`).
 *
 * Mirrors the `StatusSummaryDialog` pattern (manual overlay + centred
 * card, ESC + backdrop close). Kept local to `team-of-agents/` because
 * it carries pane-specific sizing/affordances; the rest of the SPA can
 * keep using its own dialogs.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §K-bis.
 */
import { useEffect } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogCloseBtnCls,
} from "@/components/context-pane/dialog-styles";

interface DetailDialogShellProps {
  /** Mount/unmount toggle. When false, returns null (no portal/backdrop). */
  open: boolean;
  /** Backdrop click + ESC + close-button handler. */
  onClose: () => void;
  /** Aria-labelled-by target; rendered as the dialog's <h2>. */
  titleId: string;
  /** Slot for the dialog header (title + subtitle + status badge). */
  header: React.ReactNode;
  /** Slot for the scrollable body. */
  children: React.ReactNode;
  /** Slot for the bottom action row (cancel / copy / close). */
  footer?: React.ReactNode;
  /** testid prefix for the modal root (so each dialog can be located
   *  independently in e2e). */
  testid: string;
}

export function DetailDialogShell(props: DetailDialogShellProps) {
  const { t } = useTranslation();
  const { open, onClose, titleId, header, children, footer, testid } = props;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={dialogOverlayCls}>
      <button
        type="button"
        aria-label={t("detailDialog.closeAriaLabel")}
        onClick={onClose}
        data-testid={`${testid}-backdrop`}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testid}
        className={cn(
          dialogPanelCls,
          "relative flex max-h-[85vh] max-w-2xl flex-col overflow-hidden p-0",
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-cream-200/60 px-5 py-4 dark:border-coffee-700/60">
          <div className="min-w-0 flex-1">{header}</div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            aria-label={t("detailDialog.closeAriaLabel")}
            className={dialogCloseBtnCls}
          >
            <X size={16} />
          </button>
        </div>
        <div
          data-testid={`${testid}-body`}
          className="flex-1 overflow-y-auto px-5 py-4 text-sm text-coffee-700 dark:text-slate-300"
        >
          {children}
        </div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-cream-200/60 px-5 py-3 dark:border-coffee-700/60">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
